import React, { useEffect, useMemo, useState } from "react";
import { Bell } from "lucide-react";
import { supabase } from "../Services/supabaseClient";
import { useCompany } from "../context/CompanyContext";
import "./notificationsBell.css";

export default function NotificationsBell() {
  const { companyId } = useCompany();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);

  async function load() {
    if (!companyId) return;
    const { data, error } = await supabase
      .from("notifications")
      .select(
        "id,type,title,message,severity,is_read,is_resolved,created_at,entity_type,entity_id"
      )
      .eq("company_id", companyId)
      .eq("is_resolved", false)
      .order("created_at", { ascending: false })
      .limit(20);

    if (!error) setItems(data || []);
  }

  useEffect(() => {
    load();
    // realtime updates
    if (!companyId) return;

    const ch = supabase
      .channel(`notif-${companyId}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "notifications",
          filter: `company_id=eq.${companyId}`,
        },
        () => load()
      )
      .subscribe();

    return () => supabase.removeChannel(ch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const unreadCount = useMemo(
    () => items.filter((n) => !n.is_read).length,
    [items]
  );

  async function markRead(id) {
    await supabase
      .from("notifications")
      .update({ is_read: true })
      .eq("id", id)
      .eq("company_id", companyId);
    load();
  }

  async function resolve(id) {
    await supabase
      .from("notifications")
      .update({ is_resolved: true, resolved_at: new Date().toISOString() })
      .eq("id", id)
      .eq("company_id", companyId);
    load();
  }

  return (
    <div className="nb-wrap">
      <button
        className="nb-btn"
        onClick={() => setOpen((s) => !s)}
        title="Notifications"
      >
        <Bell size={18} />
        {unreadCount > 0 && <span className="nb-badge">{unreadCount}</span>}
      </button>

      {open && (
        <div className="nb-pop">
          <div className="nb-head">
            <div style={{ fontWeight: 900 }}>Notifications</div>
            <button className="nb-link" onClick={load}>
              Refresh
            </button>
          </div>

          {items.length === 0 ? (
            <div className="nb-empty">No active notifications.</div>
          ) : (
            <div className="nb-list">
              {items.map((n) => (
                <div key={n.id} className={`nb-item ${n.severity}`}>
                  <div className="nb-title">{n.title}</div>
                  <div className="nb-msg">{n.message}</div>
                  <div className="nb-meta">
                    <span>{new Date(n.created_at).toLocaleString()}</span>
                    <div className="nb-actions">
                      {!n.is_read && (
                        <button
                          className="nb-link"
                          onClick={() => markRead(n.id)}
                        >
                          Mark read
                        </button>
                      )}
                      <button className="nb-link" onClick={() => resolve(n.id)}>
                        Resolve
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
